import moment from 'moment';
import { useCallback, useMemo } from 'react';

import {
  useCheckRecordMoveMutation,
  useLazyGetRecordByIdQuery,
  useUpdateRecordMutation,
} from '@app/api/Records/api';
import { useUserPrint } from '@app/hooks/useUserPrint';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

interface MoveParams {
  recordId: number;
  newStart: string;
  newEnd: string;
  newEmployeeId: number;
  date: string;
}

interface CheckMoveResult {
  canMove: boolean;
  message: string | null;
}

interface UseCalendarRecordActionsResult {
  checkRecordMove(params: MoveParams): Promise<CheckMoveResult>;
  applyRecordMove(params: MoveParams): Promise<boolean>;
}

export const useCalendarRecordActions = (): UseCalendarRecordActionsResult => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const [checkMove] = useCheckRecordMoveMutation();
  const [updateRecord] = useUpdateRecordMutation();
  const [getRecordById] = useLazyGetRecordByIdQuery();

  const checkRecordMove = useCallback(
    async ({
      recordId,
      newStart,
      newEnd,
      newEmployeeId,
      date,
    }: MoveParams): Promise<CheckMoveResult> => {
      if (!ActiveBranchId) {
        return {
          canMove: false,
          message: null,
        };
      }

      try {
        const { CanMove, Message } = await checkMove({
          ActiveBranchId,
          data: {
            RecordId: recordId,
            MoveTimeS: moment(`${date} ${newStart}`, 'YYYY-MM-DD HH:mm').format(
              'YYYY-MM-DDTHH:mm:ss'
            ),
            MoveTimeE: moment(`${date} ${newEnd}`, 'YYYY-MM-DD HH:mm').format(
              'YYYY-MM-DDTHH:mm:ss'
            ),
            MoveEmployeeId: newEmployeeId,
            CanMove: false,
            Message: null,
          },
        }).unwrap();

        return {
          canMove: CanMove,
          message: Message,
        };
      } catch (error) {
        showNotification(error);
        return {
          canMove: false,
          message: 'Не удалось проверить возможность переноса',
        };
      }
    },
    [ActiveBranchId, checkMove, showNotification]
  );

  const applyRecordMove = useCallback(
    async ({
      recordId,
      date,
      newStart,
      newEnd,
      newEmployeeId,
    }: MoveParams): Promise<boolean> => {
      try {
        // Load the authoritative record so the move keeps all server-side data
        // (products, services, tags, flags). The lightweight journal record does
        // not carry products, and the update endpoint replaces the whole record.
        const record = await getRecordById({
          recordId,
          ActiveBranchId,
        }).unwrap();

        await updateRecord({
          ActiveBranchId,
          data: {
            ...record,
            TimeS: moment(`${date} ${newStart}`, 'YYYY-MM-DD HH:mm').format(
              'YYYY-MM-DDTHH:mm:ss'
            ),
            TimeE: moment(`${date} ${newEnd}`, 'YYYY-MM-DD HH:mm').format(
              'YYYY-MM-DDTHH:mm:ss'
            ),
            EmployeeId: newEmployeeId,
          },
        }).unwrap();

        return true;
      } catch (error) {
        showNotification(error);
        return false;
      }
    },
    [ActiveBranchId, getRecordById, updateRecord, showNotification]
  );

  const value = useMemo(
    () => ({
      checkRecordMove,
      applyRecordMove,
    }),
    [checkRecordMove, applyRecordMove]
  );

  return value;
};
